import { useState } from 'react';
import { useAuthStore } from '../stores';
import { motion } from 'framer-motion';
import {
  Shield,
  CheckCircle,
  Zap,
  Lock,
  Sparkles,
  Crown,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { Navbar } from '../components/layout/navbar';
import { Button } from '../components/ui/button';
import { GateProtectedRoute } from '../components/providers/gate-protection';

// The Talktrail Gate configuration
const TALKTRAIL_GATE_SLUG = 'talktrail-gate';
const TALKTRAIL_GATE_ID = 'OGXhucqTvoi';

function GateTestContent() {
  const { walletAddress } = useAuthStore();
  const [copied, setCopied] = useState(false);

  const copyAddress = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Success Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-green-500/20 border border-green-500/30 mb-6"
            >
              <CheckCircle className="h-12 w-12 text-green-400" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-extralight tracking-wider text-white uppercase mb-4">
              Access Granted
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Congratulations! You have successfully passed through the Neusym
              Gate. Your wallet is authorized to view this exclusive content.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center"
            >
              <Shield className="h-8 w-8 text-cyan-400 mx-auto mb-3" />
              <h3 className="text-white font-medium mb-1">Gate Protected</h3>
              <p className="text-gray-500 text-sm">
                DKG-verified access control
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center"
            >
              <Zap className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
              <h3 className="text-white font-medium mb-1">
                Instant Verification
              </h3>
              <p className="text-gray-500 text-sm">Real-time wallet check</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center"
            >
              <Lock className="h-8 w-8 text-purple-400 mx-auto mb-3" />
              <h3 className="text-white font-medium mb-1">Exclusive Access</h3>
              <p className="text-gray-500 text-sm">Whitelist-only content</p>
            </motion.div>
          </div>

          {/* Authorized Wallet Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-lg p-8 mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <Crown className="h-6 w-6 text-cyan-400" />
              <h2 className="text-xl font-light tracking-wider text-white uppercase">
                Your Authorized Wallet
              </h2>
            </div>

            <div className="bg-black/50 rounded px-4 py-3 flex items-center justify-between gap-4">
              <code className="text-cyan-400 font-mono text-sm break-all">
                {walletAddress}
              </code>
              <Button
                variant="ghost"
                size="icon"
                onClick={copyAddress}
                className="shrink-0 h-8 w-8 hover:bg-white/10"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-400" />
                )}
              </Button>
            </div>

            <p className="text-gray-500 text-sm mt-3">
              This address is registered in the{' '}
              <span className="text-white">Talktrail Gate</span> whitelist.
            </p>
          </motion.div>

          {/* Exclusive Content Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 text-center"
          >
            <Sparkles className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-light tracking-wider text-white uppercase mb-4">
              🎉 Congratulations!
            </h2>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              You have successfully gained access to this Neusym Gate protected
              content! Your wallet is on the whitelist for the{' '}
              <span className="text-cyan-400 font-medium">
                {TALKTRAIL_GATE_SLUG}
              </span>{' '}
              gate. This demonstrates the power of decentralized access control
              using Web3 technology.
            </p>

            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
              <p className="text-green-400 text-sm">
                ✅ Your wallet has been verified and you are authorized to view
                this exclusive content.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://neusym.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-medium">
                  Learn About Neusym Gate
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a
                href={`https://neusym.io/gates/${TALKTRAIL_GATE_ID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  View This Gate on Neusym
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Footer Info */}
          <div className="text-center mt-12 space-y-2">
            <p className="text-gray-600 text-sm">
              Gate Slug:{' '}
              <code className="text-gray-400">{TALKTRAIL_GATE_SLUG}</code>
            </p>
            <p className="text-gray-600 text-sm">
              Gate ID:{' '}
              <code className="text-gray-400">{TALKTRAIL_GATE_ID}</code>
            </p>
            <p className="text-gray-500 text-xs mt-4">
              Powered by <span className="text-cyan-400">Neusym</span> •
              Decentralized Access Control
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Wrap the content with gate protection
export default function GateTestPage() {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <GateProtectedRoute gateSlug={TALKTRAIL_GATE_SLUG}>
        <GateTestContent />
      </GateProtectedRoute>
    </div>
  );
}
